"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.HeatFilterDatabaseSchema = exports.HeatFilter = exports.Weights = exports.Filters = exports.RecordWithScore = exports.FieldWeight = exports.Strategy = exports.Filter = exports.IgnoreIfNull = exports.FilterKey = exports.FilterValue = void 0;
const effect_1 = require("effect");
const master_data_1 = require("../master-data");
const scoring_1 = require("../scoring");
// PostgreSQL drivers can expose jsonb either as an already parsed object or as
// its JSON string representation. Decode both at this infrastructure boundary
// while keeping the domain-facing value identical.
const RealtimeScoringDefinitionFromDatabase = effect_1.Schema.Union([
    scoring_1.RealtimeScoringDefinition,
    effect_1.Schema.fromJsonString(scoring_1.RealtimeScoringDefinition),
]);
// v3 `Schema.Date` spelled out: Effect 4 reuses that name for a Date instance, so the string
// decoding must not hide behind it (docs/effect-4-migration-plan.md §2.2).
// v4 `Schema.DateFromString` already rejects strings that parse to an invalid Date, which is what
// v3 needed `Schema.validDate` for.
const DateFromValidString = effect_1.Schema.DateFromString.annotate({ identifier: 'Date' });
const DateFromDatabase = effect_1.Schema.Union([effect_1.Schema.Date, effect_1.Schema.DateFromString]);
const OptionFromNullWithDefaultNone = (schema) => effect_1.Schema.optional(schema);
//   Schema.optionalWith(Schema.OptionFromNullishOr(schema, null), { default: () => Option.none() });
const equalToShema = effect_1.Schema.Union([
    effect_1.Schema.String,
    effect_1.Schema.Number,
    effect_1.Schema.Boolean,
    effect_1.Schema.Array(effect_1.Schema.String),
    effect_1.Schema.Array(effect_1.Schema.Number),
    DateFromValidString,
]);
const notEqualToShema = effect_1.Schema.Union([
    effect_1.Schema.String,
    effect_1.Schema.Number,
    effect_1.Schema.Boolean,
    effect_1.Schema.Array(effect_1.Schema.String),
    effect_1.Schema.Array(effect_1.Schema.Number),
    DateFromValidString,
]);
class FilterValue extends effect_1.Schema.Class('FilterValue')({
    equalTo: OptionFromNullWithDefaultNone(equalToShema),
    includes: OptionFromNullWithDefaultNone(effect_1.Schema.String),
    isInArray: OptionFromNullWithDefaultNone(effect_1.Schema.Array(effect_1.Schema.Union([effect_1.Schema.String, effect_1.Schema.Number]))),
    isNotInArray: OptionFromNullWithDefaultNone(effect_1.Schema.Array(effect_1.Schema.Union([effect_1.Schema.String, effect_1.Schema.Number]))),
    isNull: OptionFromNullWithDefaultNone(effect_1.Schema.Boolean),
    lessThan: OptionFromNullWithDefaultNone(effect_1.Schema.Union([effect_1.Schema.Number, DateFromValidString])),
    lessThanOrEqualTo: OptionFromNullWithDefaultNone(effect_1.Schema.Union([effect_1.Schema.Number, DateFromValidString])),
    moreThan: OptionFromNullWithDefaultNone(effect_1.Schema.Union([effect_1.Schema.Number, DateFromValidString])),
    moreThanOrEqualTo: OptionFromNullWithDefaultNone(effect_1.Schema.Union([effect_1.Schema.Number, DateFromValidString])),
    notEqualTo: OptionFromNullWithDefaultNone(notEqualToShema),
    includesOneOf: OptionFromNullWithDefaultNone(effect_1.Schema.Array(effect_1.Schema.Union([effect_1.Schema.String, effect_1.Schema.Number]))),
}) {
}
exports.FilterValue = FilterValue;
exports.FilterKey = effect_1.Schema.Literals(Object.keys(FilterValue.fields));
class IgnoreIfNull extends effect_1.Schema.Class('IgnoreIfNull')({
    ignoreIfNull: OptionFromNullWithDefaultNone(effect_1.Schema.Boolean),
}) {
}
exports.IgnoreIfNull = IgnoreIfNull;
class Filter extends effect_1.Schema.Class('Filter')({
    ...FilterValue.fields,
    ...IgnoreIfNull.fields,
}) {
}
exports.Filter = Filter;
exports.Strategy = effect_1.Schema.Literals(['static', 'proportional']);
class FieldWeight extends effect_1.Schema.Class('FieldWeight')({
    weight: effect_1.Schema.Number,
    condition: Filter.pipe(effect_1.Schema.withDecodingDefaultType(effect_1.Effect.sync(() => Filter.make({}))), effect_1.Schema.withConstructorDefault(effect_1.Effect.sync(() => Filter.make({})))),
    strategy: exports.Strategy,
}) {
}
exports.FieldWeight = FieldWeight;
class RecordWithScore extends effect_1.Schema.Class('RecordWithScore')({
    record: master_data_1.MasterDataSchemaEffect,
    score: effect_1.Schema.Number,
}) {
}
exports.RecordWithScore = RecordWithScore;
// v3 derived the keys with `Schema.keyof(MasterDataSchemaEffect)`; v4 has no `keyof`, so they are spelled out.
const MasterDataKey = effect_1.Schema.Literals(master_data_1.masterDataKeysEffect);
exports.Filters = effect_1.Schema.Record(MasterDataKey, effect_1.Schema.optional(Filter));
exports.Weights = effect_1.Schema.Record(MasterDataKey, effect_1.Schema.optional(effect_1.Schema.Array(FieldWeight)));
// Below casting is needed to avoid the error:
// “The inferred type of this node exceeds the maximum length the compiler will serialize. An explicit type annotation is needed.ts(7056)”
const FiltersHelper = effect_1.Schema.optional(exports.Filters);
const WeightsHelper = effect_1.Schema.optional(exports.Weights);
class HeatFilter extends effect_1.Schema.Class('HeatFilter')({
    // Use the helper variables directly to avoid serialization issues
    filters: FiltersHelper,
    weights: WeightsHelper,
}) {
}
exports.HeatFilter = HeatFilter;
const HeatFilterFromDatabase = effect_1.Schema.Union([HeatFilter, effect_1.Schema.fromJsonString(HeatFilter)]);
class HeatFilterDatabaseRecord extends effect_1.Schema.Class('HeatFilterDatabaseSchema')({
    heatFilterId: effect_1.Schema.String.check(effect_1.Schema.isGUID()),
    name: effect_1.Schema.String,
    clientId: effect_1.Schema.OptionFromNullOr(effect_1.Schema.String.check(effect_1.Schema.isGUID())),
    configuration: effect_1.Schema.OptionFromNullOr(HeatFilterFromDatabase),
    isPrivate: effect_1.Schema.Boolean,
    sqlConfiguration: effect_1.Schema.OptionFromNullOr(effect_1.Schema.String),
    /** Present only for filters prepared for the new realtime scoring engine. */
    definitionV2: effect_1.Schema.OptionFromNullOr(RealtimeScoringDefinitionFromDatabase),
    /** Keeps legacy, manual SQL and metric-v1 execution paths explicit during migration. */
    executionMode: effect_1.Schema.Literals(['legacy_live', 'metric_v1', 'manual_sql']),
    /** Increments only when a realtime definition or its execution mode changes. */
    revision: effect_1.Schema.Number,
    /** Timestamp of the last material filter update. */
    updatedAt: DateFromDatabase,
}) {
}
/** A `heat_filter` row: snake_case columns decoded into a {@link HeatFilterDatabaseRecord}. */
exports.HeatFilterDatabaseSchema = HeatFilterDatabaseRecord.pipe(effect_1.Schema.encodeKeys({
    heatFilterId: 'heat_filter_id',
    clientId: 'client_id',
    isPrivate: 'is_private',
    sqlConfiguration: 'sql_configuration',
    definitionV2: 'definition_v2',
    executionMode: 'execution_mode',
    updatedAt: 'updated_at',
}));
//# sourceMappingURL=effect.js.map